import React, { useState, useEffect, useRef, useCallback } from "react"
import Typography from "aldoo-ra/Typography"
import ContentEditor from "aldoo-ra/CMS/content-editor"
import { showNotification } from "aldoo-ra/Notification"
import { UpdateParam, CreateParam } from "../../ParamIO/api"
import { HistoryAPI } from "../history-api"
import HistorySettingsModel from "aldoo-ra/CMSModels/HistorySettings"

/**
 * Settings Tab - Configure history tracking settings
 */
const SettingsTab = () => {
  const [fieldValues, setFieldValues] = useState({})
  const [loading, setLoading] = useState(true)
  const [originalValues, setOriginalValues] = useState({})
  const [paramDefinitions, setParamDefinitions] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const saveTimeoutRef = useRef(null)

  // Load current settings
  const loadSettings = async () => {
    try {
      setLoading(true)

      const response = await HistoryAPI.GetConfig()

      if (response.result) {
        setFieldValues(response.config)
        setOriginalValues(response.config)
        setParamDefinitions(response.paramDefinitions || {})
      } else {
        showNotification({
          message: "Failed to load settings",
          className: "bg-admin_error text-white"
        })
      }
    } catch (error) {
      console.error("Failed to load settings:", error)
      showNotification({
        message: "Failed to load settings",
        className: "bg-admin_error text-white"
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadSettings()
  }, [])

  // Auto-save with debounce when field values change
  useEffect(() => {
    // Skip if still loading initial values
    if (loading || Object.keys(originalValues).length === 0) return

    // Check if any values have changed
    const hasChanges = Object.keys(fieldValues).some(key =>
      JSON.stringify(fieldValues[key]) !== JSON.stringify(originalValues[key])
    )

    if (!hasChanges) return

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    // Set new timeout to save after 1 second of no changes
    saveTimeoutRef.current = setTimeout(() => {
      handleSave(fieldValues)
    }, 1000)

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [fieldValues, originalValues, loading])

  // Save settings
  const handleSave = async (values) => {
    if (isSaving) return // Prevent concurrent saves

    try {
      setIsSaving(true)
      const savePromises = []

      // Save each changed setting
      Object.entries(values).forEach(([fieldId, value]) => {
        const originalValue = originalValues[fieldId]

        // Compare values
        const hasChanged = JSON.stringify(value) !== JSON.stringify(originalValue)

        if (hasChanged) {
          const definition = paramDefinitions[fieldId]
          if (!definition || !definition.address) {
            console.warn(`No param definition found for ${fieldId}`)
            return
          }

          // Try to update existing param, create if doesn't exist
          const savePromise = UpdateParam({
            address: definition.address,
            value
          }).catch(async (error) => {
            // If update fails, try to create
            return CreateParam({
              address: definition.address,
              value
            })
          })

          savePromises.push(savePromise)
        }
      })

      if (savePromises.length === 0) {
        setIsSaving(false)
        return
      }

      await Promise.all(savePromises)

      // Update original values to reflect saved state
      setOriginalValues(values)

      showNotification({
        message: "Settings saved",
        className: "bg-admin_success text-white"
      })
    } catch (error) {
      console.error("Failed to save settings:", error)
      showNotification({
        message: "Failed to save settings",
        className: "bg-admin_error text-white"
      })
    } finally {
      setIsSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Typography variant="admin_txt1_dim">
          Loading settings...
        </Typography>
      </div>
    )
  }

  return (
    <div className="h-screen bg-admin_canvas text-admin_text flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 bg-admin_paper border-b border-admin_bordered p-4">
        <div className="flex flex-col">
          <Typography 
            component="h1"
            variant="admin_h4"
          >
            Settings
          </Typography>

          <Typography 
            variant="admin_body_dim" 
            className="my-0"
          >
            Configure history tracking behavior and retention policies
          </Typography>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-6xl bg-admin_paper rounded-3xl p-3">
          {Object.keys(fieldValues).length > 0 ? (
            <ContentEditor
              contentType={HistorySettingsModel}
              dataItem={{ content: fieldValues }}
              fieldValues={fieldValues}
              onFieldChange={(fieldId, value) => {
                setFieldValues(prev => ({
                  ...prev,
                  [fieldId]: value
                }))
              }}
              onSave={handleSave}
              onError={(errors) => {
                showNotification({
                  message: Object.values(errors).find((e) => e) || "Validation error",
                  className: "bg-admin_error text-white"
                })
              }}
              variant="admin"
              hideSaveButton={true}
              hidePanel={true}
              hideHeader={true}
              showSearch={false}
            />
          ) : (
            <div className="bg-admin_paper rounded-lg p-6">
              <Typography variant="admin_txt2_dim">
                Loading settings...
              </Typography>
            </div>
          )}
          
          {isSaving && (
            <div className="fixed bottom-4 right-4 bg-admin_primary text-admin_text_inv px-4 py-2 rounded-lg shadow-lg">
              Saving...
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SettingsTab
